// Thema-overzichtspagina's (/thema/<slug>) — informatieve, NAVIGERENDE
// publiekscontent. MDR-wordingregels (zie compliance/02-ai-act +
// 2026-07-02-wettelijke-gap-analyse.md):
// - NOOIT symptomen of ernst interpreteren of "beoordelen";
// - geen test-/screening-/diagnoseframing;
// - klachten beschrijven als redenen waarom mensen hulp zoeken, niet als
//   aandoeningen die wij "behandelen" of "herkennen";
// - geen resultaatgaranties; altijd crisis-verwijzing (1813/106/112).

import { Lang } from "./mutualiteiten";

export interface ThemeSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface ThemeContent {
  title: string;
  lead: string;
  sections: ThemeSection[];
}

export interface ThemeDef {
  slug: string;
  /** Korte naam voor menu's/footers, per taal. */
  nav: Record<Lang, string>;
  content: Record<Lang, ThemeContent>;
}

export const THEMES: ThemeDef[] = [
  {
    slug: "werkstress-burnout",
    nav: { nl: "Werkstress & burn-out", fr: "Stress au travail & burn-out", en: "Work stress & burnout" },
    content: {
      nl: {
        title: "Werkstress & burn-out",
        lead: "Wanneer werk meer energie vraagt dan het teruggeeft, kan praten met een psycholoog helpen om overzicht en ademruimte terug te vinden. Deze pagina legt uit waarvoor mensen bij dit thema hulp zoeken en welke stappen je kunt zetten — ze geeft geen beoordeling van jouw situatie.",
        sections: [
          {
            heading: "Waarvoor mensen hulp zoeken",
            paragraphs: ["Iedereen ervaart werkdruk anders. Redenen waarom mensen rond dit thema een gesprek starten, zijn bijvoorbeeld:"],
            bullets: [
              "aanhoudend het gevoel hebben achter de feiten aan te lopen",
              "moeilijk kunnen loskomen van het werk, ook 's avonds of in het weekend",
              "twijfels over grenzen stellen tegenover collega's of leidinggevenden",
              "na een periode van overbelasting opnieuw willen opbouwen",
              "de vraag of je job nog bij je past",
            ],
          },
          {
            heading: "Wat een psycholoog met je kan doen",
            paragraphs: [
              "Een psycholoog beoordeelt samen met jou wat er speelt en wat jij nodig hebt — dat gesprek vervangt deze pagina niet. Afhankelijk van je situatie kan de begeleiding gaan over energiebronnen en stressoren in kaart brengen, grenzen leren aangeven, herstel opbouwen of keuzes rond werk verkennen. Bij Carefunded kies je tussen doelgerichte coaching en therapie; de gratis kennismaking helpt je bepalen wat past.",
              "Twijfel je of je klachten (ook) lichamelijk zijn, of ben je langdurig uitgevallen? Betrek dan zeker ook je huisarts of arbeidsarts — zij zijn in België de aangewezen weg voor medische beoordeling en re-integratie.",
            ],
          },
          {
            heading: "Zelf aan de slag",
            paragraphs: [
              "In onze zelfhulpomgeving vind je trajecten met korte lessen en oefeningen rond onder meer stress en veerkracht — op je eigen tempo, gratis te verkennen. Zelfhulp is algemene informatie en geen vervanging voor professionele hulp.",
              "Ben je al in begeleiding? Je psycholoog kan je een verdiepend traject rond dit thema toewijzen.",
            ],
          },
        ],
      },
      fr: {
        title: "Stress au travail & burn-out",
        lead: "Quand le travail demande plus d'énergie qu'il n'en redonne, parler avec un psychologue peut aider à retrouver de la clarté et de l'air. Cette page explique pourquoi les gens cherchent de l'aide autour de ce thème et quelles étapes vous pouvez franchir — elle n'évalue pas votre situation.",
        sections: [
          {
            heading: "Pourquoi les gens cherchent de l'aide",
            paragraphs: ["Chacun vit la pression du travail différemment. Voici des raisons pour lesquelles des personnes entament un entretien autour de ce thème :"],
            bullets: [
              "avoir constamment le sentiment de courir après les choses",
              "avoir du mal à décrocher du travail, même le soir ou le week-end",
              "des doutes sur la manière de poser des limites face aux collègues ou responsables",
              "vouloir se reconstruire après une période de surcharge",
              "la question de savoir si votre emploi vous convient encore",
            ],
          },
          {
            heading: "Ce qu'un psychologue peut faire avec vous",
            paragraphs: [
              "Le psychologue évalue avec vous ce qui se passe et ce dont vous avez besoin — cet entretien ne remplace pas cette page. Selon votre situation, l'accompagnement peut porter sur l'identification des sources d'énergie et de stress, l'apprentissage des limites, la reconstruction ou l'exploration de choix professionnels. Chez Carefunded, vous choisissez entre coaching ciblé et thérapie ; la première rencontre gratuite vous aide à déterminer ce qui convient.",
              "Vous vous demandez si vos plaintes sont (aussi) physiques, ou vous êtes en incapacité de longue durée ? Impliquez alors votre médecin généraliste ou votre médecin du travail — en Belgique, ils sont la voie indiquée pour l'évaluation médicale et la réintégration.",
            ],
          },
          {
            heading: "Agir par vous-même",
            paragraphs: [
              "Dans notre espace d'auto-assistance, vous trouvez des parcours avec de courtes leçons et exercices autour notamment du stress et de la résilience — à votre rythme, à explorer gratuitement. L'auto-assistance est une information générale et ne remplace pas une aide professionnelle.",
              "Vous êtes déjà accompagné·e ? Votre psychologue peut vous attribuer un parcours d'approfondissement autour de ce thème.",
            ],
          },
        ],
      },
      en: {
        title: "Work stress & burnout",
        lead: "When work takes more energy than it gives back, talking to a psychologist can help you regain overview and breathing space. This page explains why people seek help around this theme and which steps you can take — it does not assess your situation.",
        sections: [
          {
            heading: "Why people seek help",
            paragraphs: ["Everyone experiences work pressure differently. Reasons why people start a conversation around this theme include:"],
            bullets: [
              "constantly feeling like you're running behind",
              "finding it hard to disconnect from work, even in the evening or at weekends",
              "doubts about setting boundaries with colleagues or managers",
              "wanting to rebuild after a period of overload",
              "wondering whether your job still fits you",
            ],
          },
          {
            heading: "What a psychologist can do with you",
            paragraphs: [
              "A psychologist assesses together with you what is going on and what you need — that conversation is not replaced by this page. Depending on your situation, the support can cover mapping energy sources and stressors, learning to set boundaries, building recovery or exploring work choices. At Carefunded you choose between goal-oriented coaching and therapy; the free introduction helps you decide what fits.",
              "Wondering whether your complaints are (also) physical, or have you been off work for a longer period? Then also involve your GP or occupational physician — in Belgium they are the designated route for medical assessment and reintegration.",
            ],
          },
          {
            heading: "Working on it yourself",
            paragraphs: [
              "Our self-help environment offers programmes with short lessons and exercises around, among other things, stress and resilience — at your own pace, free to explore. Self-help is general information and no substitute for professional help.",
              "Already receiving support? Your psychologist can assign you a deepening programme around this theme.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "piekeren-spanning",
    nav: { nl: "Piekeren & spanning", fr: "Ruminations & tension", en: "Worrying & tension" },
    content: {
      nl: {
        title: "Piekeren & spanning",
        lead: "Malen, onrust of spanning die maar niet zakt: het zijn veelvoorkomende redenen om een gesprek met een psycholoog te starten. Deze pagina wijst je de weg — ze interpreteert of beoordeelt je klachten niet.",
        sections: [
          {
            heading: "Waarvoor mensen hulp zoeken",
            paragraphs: ["Redenen waarom mensen rond dit thema hulp zoeken, zijn bijvoorbeeld:"],
            bullets: [
              "gedachten die blijven malen, ook 's nachts",
              "onrust of spanning in het lichaam",
              "situaties uit de weg gaan die spanning oproepen",
              "moeite met beslissingen door blijven twijfelen",
              "de behoefte om rustiger met zorgen om te gaan",
            ],
          },
          {
            heading: "Wat een psycholoog met je kan doen",
            paragraphs: [
              "In een eerste gesprek verken je samen wat er speelt, hoe het je dagelijks leven beïnvloedt en welke aanpak daarbij past. Psychologen werken rond dit thema onder meer met inzicht in piekerpatronen, oefeningen om tot rust te komen en het stap voor stap opnieuw opzoeken van situaties die je liever vermijdt — altijd op jouw tempo en zonder vooraf beloofd resultaat.",
              "Zijn er ook lichamelijke klachten, of twijfel je waar je het best start? Je huisarts kan mee bekijken wat er nodig is.",
            ],
          },
          {
            heading: "Zelf aan de slag",
            paragraphs: [
              "Probeer de ademhalingsoefening op ons platform of verken de zelfhulptrajecten rond rust en veerkracht. Zelfhulp is algemene informatie en geen vervanging voor professionele hulp.",
              "Ben je al in begeleiding? Je psycholoog kan je een verdiepend traject rond dit thema toewijzen.",
            ],
          },
        ],
      },
      fr: {
        title: "Ruminations & tension",
        lead: "Ressasser, être agité ou tendu sans que cela retombe : ce sont des raisons fréquentes d'entamer un entretien avec un psychologue. Cette page vous oriente — elle n'interprète ni n'évalue vos plaintes.",
        sections: [
          {
            heading: "Pourquoi les gens cherchent de l'aide",
            paragraphs: ["Voici des raisons pour lesquelles des personnes cherchent de l'aide autour de ce thème :"],
            bullets: [
              "des pensées qui tournent en boucle, même la nuit",
              "de l'agitation ou de la tension dans le corps",
              "éviter des situations qui génèrent de la tension",
              "des difficultés à décider à force de douter",
              "le besoin de gérer plus sereinement les soucis",
            ],
          },
          {
            heading: "Ce qu'un psychologue peut faire avec vous",
            paragraphs: [
              "Lors d'un premier entretien, vous explorez ensemble ce qui se passe, l'impact sur votre quotidien et l'approche qui convient. Autour de ce thème, les psychologues travaillent notamment sur la compréhension des schémas de rumination, des exercices d'apaisement et la reprise progressive de situations évitées — toujours à votre rythme et sans résultat promis d'avance.",
              "Il y a aussi des plaintes physiques, ou vous hésitez sur le meilleur point de départ ? Votre médecin généraliste peut examiner avec vous ce qui est nécessaire.",
            ],
          },
          {
            heading: "Agir par vous-même",
            paragraphs: [
              "Essayez l'exercice de respiration sur notre plateforme ou explorez les parcours d'auto-assistance autour du calme et de la résilience. L'auto-assistance est une information générale et ne remplace pas une aide professionnelle.",
              "Vous êtes déjà accompagné·e ? Votre psychologue peut vous attribuer un parcours d'approfondissement autour de ce thème.",
            ],
          },
        ],
      },
      en: {
        title: "Worrying & tension",
        lead: "Rumination, restlessness or tension that won't fade: these are common reasons to start a conversation with a psychologist. This page points the way — it does not interpret or assess your complaints.",
        sections: [
          {
            heading: "Why people seek help",
            paragraphs: ["Reasons why people seek help around this theme include:"],
            bullets: [
              "thoughts that keep churning, even at night",
              "restlessness or tension in the body",
              "avoiding situations that trigger tension",
              "difficulty deciding because of persistent doubt",
              "wanting to handle worries more calmly",
            ],
          },
          {
            heading: "What a psychologist can do with you",
            paragraphs: [
              "In a first conversation you explore together what is going on, how it affects your daily life and which approach fits. Around this theme psychologists work with, among other things, insight into worry patterns, calming exercises and gradually re-approaching situations you'd rather avoid — always at your pace and without a promised outcome.",
              "Are there physical complaints too, or are you unsure where to start? Your GP can help look at what is needed.",
            ],
          },
          {
            heading: "Working on it yourself",
            paragraphs: [
              "Try the breathing exercise on our platform or explore the self-help programmes around calm and resilience. Self-help is general information and no substitute for professional help.",
              "Already receiving support? Your psychologist can assign you a deepening programme around this theme.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "relaties",
    nav: { nl: "Relaties & verbinding", fr: "Relations & lien", en: "Relationships & connection" },
    content: {
      nl: {
        title: "Relaties & verbinding",
        lead: "Spanningen met je partner, familie, vrienden of collega's — of je net eenzaam voelen: relaties zijn een van de meest voorkomende redenen om met een psycholoog te praten. Deze pagina wijst je de weg.",
        sections: [
          {
            heading: "Waarvoor mensen hulp zoeken",
            paragraphs: ["Redenen waarom mensen rond dit thema een gesprek starten, zijn bijvoorbeeld:"],
            bullets: [
              "terugkerende conflicten of stroeve communicatie met je partner",
              "een relatiebreuk of scheiding verwerken",
              "spanningen in het gezin of met familie",
              "je eenzaam voelen, ook tussen andere mensen",
              "patronen die je in meerdere relaties ziet terugkomen",
            ],
          },
          {
            heading: "Wat een psycholoog met je kan doen",
            paragraphs: [
              "Een psycholoog bekijkt samen met jou wat er in jouw relaties speelt en wat je eraan wilt veranderen. Dat kan individueel: aan je eigen aandeel, grenzen en verwachtingen werken helpt vaak ook als de ander niet meekomt. In de gratis kennismaking bespreek je wat voor jouw situatie zinvol is.",
              "Zoek je specifiek begeleiding samen met je partner, geef dat dan aan bij de intake — dan zoeken we een psycholoog die daarmee vertrouwd is.",
            ],
          },
          {
            heading: "Zelf aan de slag",
            paragraphs: [
              "In de zelfhulpomgeving vind je trajecten rond onder meer verbinding en communicatie. Zelfhulp is algemene informatie en geen vervanging voor professionele hulp.",
              "Ben je al in begeleiding? Je psycholoog kan je een verdiepend traject rond dit thema toewijzen.",
            ],
          },
        ],
      },
      fr: {
        title: "Relations & lien",
        lead: "Tensions avec votre partenaire, votre famille, vos amis ou collègues — ou au contraire un sentiment de solitude : les relations sont l'une des raisons les plus fréquentes de parler avec un psychologue. Cette page vous oriente.",
        sections: [
          {
            heading: "Pourquoi les gens cherchent de l'aide",
            paragraphs: ["Voici des raisons pour lesquelles des personnes entament un entretien autour de ce thème :"],
            bullets: [
              "des conflits récurrents ou une communication difficile avec votre partenaire",
              "digérer une rupture ou une séparation",
              "des tensions dans la famille",
              "se sentir seul·e, même entouré·e",
              "des schémas qui reviennent dans plusieurs relations",
            ],
          },
          {
            heading: "Ce qu'un psychologue peut faire avec vous",
            paragraphs: [
              "Le psychologue examine avec vous ce qui se joue dans vos relations et ce que vous voulez changer. Cela peut se faire individuellement : travailler sur votre propre part, vos limites et vos attentes aide souvent même si l'autre ne participe pas. Lors de la première rencontre gratuite, vous discutez de ce qui a du sens pour votre situation.",
              "Vous cherchez spécifiquement un accompagnement avec votre partenaire ? Indiquez-le lors de l'intake — nous chercherons un psychologue qui y est familiarisé.",
            ],
          },
          {
            heading: "Agir par vous-même",
            paragraphs: [
              "Dans l'espace d'auto-assistance, vous trouvez des parcours autour notamment du lien et de la communication. L'auto-assistance est une information générale et ne remplace pas une aide professionnelle.",
              "Vous êtes déjà accompagné·e ? Votre psychologue peut vous attribuer un parcours d'approfondissement autour de ce thème.",
            ],
          },
        ],
      },
      en: {
        title: "Relationships & connection",
        lead: "Tension with your partner, family, friends or colleagues — or feeling lonely: relationships are one of the most common reasons to talk to a psychologist. This page points the way.",
        sections: [
          {
            heading: "Why people seek help",
            paragraphs: ["Reasons why people start a conversation around this theme include:"],
            bullets: [
              "recurring conflict or difficult communication with your partner",
              "processing a break-up or divorce",
              "tension within the family",
              "feeling lonely, even among other people",
              "patterns you see returning across relationships",
            ],
          },
          {
            heading: "What a psychologist can do with you",
            paragraphs: [
              "A psychologist looks together with you at what is happening in your relationships and what you want to change. That can be individual: working on your own part, boundaries and expectations often helps even when the other person doesn't join. In the free introduction you discuss what makes sense for your situation.",
              "Looking specifically for support together with your partner? Mention it during the intake — we'll look for a psychologist familiar with that.",
            ],
          },
          {
            heading: "Working on it yourself",
            paragraphs: [
              "The self-help environment offers programmes around, among other things, connection and communication. Self-help is general information and no substitute for professional help.",
              "Already receiving support? Your psychologist can assign you a deepening programme around this theme.",
            ],
          },
        ],
      },
    },
  },
  {
    slug: "rouw-verlies",
    nav: { nl: "Rouw & verlies", fr: "Deuil & perte", en: "Grief & loss" },
    content: {
      nl: {
        title: "Rouw & verlies",
        lead: "Het verlies van een dierbare, je gezondheid, een relatie of een job: rouwen is geen probleem dat 'opgelost' moet worden, maar soms helpt het om er met een professional over te praten. Deze pagina wijst je de weg.",
        sections: [
          {
            heading: "Waarvoor mensen hulp zoeken",
            paragraphs: ["Rouw verloopt voor iedereen anders, en er bestaat geen juiste manier of termijn. Redenen waarom mensen rond dit thema een gesprek starten, zijn bijvoorbeeld:"],
            bullets: [
              "het gemis van een overleden dierbare dragen",
              "een ingrijpende verandering verwerken, zoals ziekte of jobverlies",
              "het gevoel dat de omgeving verwacht dat je 'er al over bent'",
              "vastlopen in het dagelijks leven na een verlies",
              "steun zoeken bij het afscheid dat er nog aankomt",
            ],
          },
          {
            heading: "Wat een psycholoog met je kan doen",
            paragraphs: [
              "Een psycholoog biedt een rustige plek om stil te staan bij je verlies, op jouw tempo. De begeleiding kan gaan over ruimte maken voor gemis, opnieuw structuur vinden of omgaan met reacties uit je omgeving. Wat passend is, bepaal je samen — er is geen vast programma en geen beloofd resultaat.",
              "Maak je je zorgen over hoe je eraan toe bent, praat er dan ook met je huisarts over.",
              "Ben je al in begeleiding? Je psycholoog kan je een verdiepend traject rond dit thema toewijzen.",
            ],
          },
          {
            heading: "Als het te zwaar wordt",
            paragraphs: [
              "Denk je aan zelfdoding of zie je geen uitweg meer? Bel dan nu 1813 (Zelfmoordlijn, gratis en 24/7) of 112 bij acuut gevaar. Anoniem praten kan ook bij Tele-Onthaal op 106.",
            ],
          },
        ],
      },
      fr: {
        title: "Deuil & perte",
        lead: "La perte d'un proche, de votre santé, d'une relation ou d'un emploi : le deuil n'est pas un problème à « résoudre », mais il aide parfois d'en parler avec un professionnel. Cette page vous oriente.",
        sections: [
          {
            heading: "Pourquoi les gens cherchent de l'aide",
            paragraphs: ["Le deuil est différent pour chacun, et il n'existe ni bonne manière ni bon délai. Voici des raisons pour lesquelles des personnes entament un entretien autour de ce thème :"],
            bullets: [
              "porter le manque d'un proche décédé",
              "digérer un changement majeur, comme une maladie ou la perte d'un emploi",
              "le sentiment que l'entourage attend que vous ayez « tourné la page »",
              "se sentir bloqué·e dans la vie quotidienne après une perte",
              "chercher du soutien face à un adieu qui approche",
            ],
          },
          {
            heading: "Ce qu'un psychologue peut faire avec vous",
            paragraphs: [
              "Le psychologue offre un espace calme pour vous arrêter sur votre perte, à votre rythme. L'accompagnement peut porter sur faire de la place au manque, retrouver une structure ou gérer les réactions de l'entourage. Ce qui convient, vous le déterminez ensemble — il n'y a ni programme fixe ni résultat promis.",
              "Vous vous inquiétez de votre état ? Parlez-en aussi à votre médecin généraliste.",
              "Vous êtes déjà accompagné·e ? Votre psychologue peut vous attribuer un parcours d'approfondissement autour de ce thème.",
            ],
          },
          {
            heading: "Si cela devient trop lourd",
            paragraphs: [
              "Vous pensez au suicide ou ne voyez plus d'issue ? Appelez maintenant le Centre de prévention du suicide (0800 32 123, gratuit et 24/7), le 1813 ou le 112 en cas de danger immédiat. Vous pouvez aussi parler anonymement à Télé-Accueil au 107.",
            ],
          },
        ],
      },
      en: {
        title: "Grief & loss",
        lead: "Losing a loved one, your health, a relationship or a job: grief is not a problem to be 'solved', but sometimes it helps to talk about it with a professional. This page points the way.",
        sections: [
          {
            heading: "Why people seek help",
            paragraphs: ["Grief is different for everyone, and there is no right way or timeline. Reasons why people start a conversation around this theme include:"],
            bullets: [
              "carrying the absence of someone who died",
              "processing a major change, such as illness or losing a job",
              "feeling that others expect you to have 'moved on' already",
              "getting stuck in daily life after a loss",
              "seeking support for a farewell that is still to come",
            ],
          },
          {
            heading: "What a psychologist can do with you",
            paragraphs: [
              "A psychologist offers a calm place to dwell on your loss, at your own pace. The support can be about making room for the absence, finding structure again or dealing with reactions from those around you. What fits is something you decide together — there is no fixed programme and no promised outcome.",
              "Worried about how you are doing? Talk to your GP about it as well.",
              "Already receiving support? Your psychologist can assign you a deepening programme around this theme.",
            ],
          },
          {
            heading: "If it becomes too heavy",
            paragraphs: [
              "Are you thinking about suicide or do you see no way out? Call 1813 (suicide prevention line, free and 24/7) now, or 112 in immediate danger. You can also talk anonymously to Tele-Onthaal on 106.",
            ],
          },
        ],
      },
    },
  },
];
